import { Colors } from '@src/colors';
import PropTypes from 'prop-types';
import React from 'react';
import * as styled from './wire-icon.styled';

/**
 * Create SVG element that points to globally defined wire icons. This depends
 * on loading `//{{ STATIC_ASSETS_DOMAIN }}/icon/ui-icons.svg` in the
 * Django base template.
 *
 * To color the icon, set `colorName` to one of the standard colors defined in
 * `base-style-lib`. This works because those colors are copied to CSS3
 * variables that can be referenced by an inline style.
 *
 * Alternatively, apply a custom CSS class with a stroke rule:
 * @example stroke: map-get($vacasa-colors, "moss")
 *
 * About the `use` tag in React:
 * @see https://github.com/facebook/react/issues/1657#issuecomment-197052674
 *
 * @type {React.FunctionComponent}
 * @param {Object} props
 * @param {string} props.svg ID of SVG elements injected at top of body
 * @param {number} [props.tabIndex]
 * @param {string} [props.className] Optional CSS class(es) to include
 * @param {string} [props.colorName] Standard color from `/base_style-lib/bootstrap/scss/mixins/_variables.scss`
 * @param {string} [props.ariaLabel]
 * @param {(e?: React.MouseEvent) => void} [props.onClick] Method called when icon is clicked
 */
export const WireIcon = ({
    dataCy,
    svg,
    tabIndex,
    className,
    colorName,
    onClick,
    ariaLabel,
}: {
    dataCy?: string;
    svg: string;
    tabIndex: number;
    className: string;
    colorName?: Colors;
    ariaLabel: string;
    onClick?: (e?: React.MouseEvent) => void;
}): JSX.Element => {
    if (!svg) return <></>;

    let css = 'v-ui-icon';
    const prefix = 'v-icon-ui-';
    if (typeof onClick === 'function') css += ' clickable';
    if (className) css += ' ' + className;

    const style = colorName ? { fill: colorName } : undefined;

    return (
        <styled.SVG
            data-cy={dataCy}
            onClick={onClick}
            onKeyPress={onClick as any}
            role={onClick ? 'button' : undefined}
            aria-label={ariaLabel}
            style={style}
            tabIndex={tabIndex}
            className={css}
        >
            <use xlinkHref={'#' + prefix + svg} />
        </styled.SVG>
    );
};

WireIcon.propTypes = {
    svg: PropTypes.string.isRequired,
    className: PropTypes.string,
    colorName: PropTypes.string,
    tabIndex: PropTypes.number,
    onClick: PropTypes.func,
    ariaLabel: PropTypes.string,
};

WireIcon.defaultProps = {
    className: undefined,
    colorName: undefined,
    onClick: undefined,
    tabIndex: undefined,
    ariaLabel: undefined,
};
